import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';

export const INTUITIVE_INDICATION_BRIDGE_OFFSET = 100;

/**
 * Unified indication ids for the intuitive teeth diagram: standard {@link UnitTypes}
 * plus bridge-specific {@link UnitTypesInBridge} values offset by +100 so numeric ranges never overlap.
 */
export enum IntuitiveIndication {
	// UnitTypes
	Regular = 1,
	Crown = 2,
	Crown_ThreeQuarters = 3,
	ImplantAbutment = 4,
	Inlay = 5,
	Onlay = 6,
	Veneer = 7,
	Detachable = 8,
	Missing_edentulousSpace = 9,
	Missing_noSpace = 10,
	Deciduous = 11,
	ScanBody = 12,
	CerecGuide = 13,
	ImplantBased = 14,
	SupportingTooth = 15,
	ToBeRemoved = 16,
	Clasp = 17,
	ImplantPosition = 18,
	VeneerOrLaminates = 19,
	Missing = 21,
	EggshellCrown = 22,
	MockupTooth = 23,

	Bridge = 100,
	// UnitTypesInBridge (+100);
	Abutment = 101,
	MarylandAbutment = 103,
	Pontic = 104,
	Skipped = 105,
	BridgeCrown = 106,
	BridgeImplantAbutment = 108,
	BridgeScanBody = 109,
	BridgeInlay = 110,
	BridgeOnlay = 111,
	BridgeVeneer = 112,
	BridgeMissing = 113,
	Maryland = 114,
	BridgeImplantBased = 115,
	EggshellBridge = 116,
	MockupBridge = 117
}

const isNumericEnumValue = (enumObject: object, n: number): boolean => typeof (enumObject as Record<number, unknown>)[n] === 'string';

export const toUnitType = (indication: IntuitiveIndication | number): UnitTypes | null =>
	isNumericEnumValue(UnitTypes, indication) ? (indication as UnitTypes) : null;

export const toUnitTypeOrDefault = (
	indication: IntuitiveIndication | number | null | undefined,
	defaultValue: UnitTypes = UnitTypes.Regular
): UnitTypes => {
	if (indication == null) {
		return defaultValue;
	}

	return isNumericEnumValue(UnitTypes, indication) ? (indication as UnitTypes) : defaultValue;
};

export const toUnitTypeInBridge = (indication: IntuitiveIndication | number): UnitTypesInBridge | null => {
	if (indication < INTUITIVE_INDICATION_BRIDGE_OFFSET) {
		return null;
	}

	const bridgeIndication = indication - INTUITIVE_INDICATION_BRIDGE_OFFSET;

	return isNumericEnumValue(UnitTypesInBridge, bridgeIndication) ? (bridgeIndication as UnitTypesInBridge) : null;
};

export const fromUnitType = (indication: UnitTypes | number | undefined | null): IntuitiveIndication | null => {
	if (!indication) {
		return null;
	}

	return isNumericEnumValue(IntuitiveIndication, indication) ? (indication as IntuitiveIndication) : null;
};

export const fromUnitTypeInBridge = (indication: UnitTypesInBridge | number | undefined | null): IntuitiveIndication | null => {
	if (!indication) {
		return null;
	}

	const bridgeIndication = indication + INTUITIVE_INDICATION_BRIDGE_OFFSET;

	return isNumericEnumValue(IntuitiveIndication, bridgeIndication) ? (bridgeIndication as IntuitiveIndication) : null;
};
